import { type FC, useMemo } from "react";
import { useParams } from "react-router-dom";
import styled from "@emotion/styled";
import { Visible } from "../../common/components";
import { ApiErrorNotice } from "../../insights/components/chart/ApiErrorNotice";
import { useOrganizations } from "../../organizations/hooks";
import { type RouteParams, AbsoluteRoute } from "../../../core/routes";
import { AppList } from "../components/AppList";
import { TitlePane } from "../dashboard/TitlePane";
import { useApps } from "../hooks/useApps";
import { topNavigationHeight } from "../../../layout/components";
import { CardWithBannerSkeletonContainer } from "../../dashboard/components/CardWithBannerSkeletonContainer";
import { EmptyAppContainer } from "./EmptyAppContainer";

export const AppListContainer: FC = () => {
    const { orgId } = useParams<RouteParams[AbsoluteRoute.ORG_APPS]>();
    const { loading, apps, error } = useApps({});
    const { loading: isOrgsLoading } = useOrganizations();

    const queriesLoading = useMemo(() => loading || isOrgsLoading, [isOrgsLoading, loading]);

    if (error) {
        return (
            <CenteredApiErrorNotice>
                <ApiErrorNotice error={error} children={<></>} />
            </CenteredApiErrorNotice>
        );
    }

    return (
        <CardWithBannerSkeletonContainer loading={queriesLoading}>
            <TitlePane title="Apps" hasApps={apps.length > 0} organizationId={orgId ?? ""} />
            <Visible when={!apps.length && !queriesLoading}>
                <EmptyAppContainer />
            </Visible>
            <Visible when={apps.length > 0}>
                <AppList apps={apps} isLoading={loading} />
            </Visible>
        </CardWithBannerSkeletonContainer>
    );
};

const CenteredApiErrorNotice = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: calc(100vh - ${topNavigationHeight} - 1px);
`;
